/**
 * Orders Filter Configuration
 *
 * Domain-specific filter configuration for URL state management.
 * Defines all filterable fields, their types, defaults, and serialization.
 */

import { createFilterConfig, type FilterFieldConfig } from '@spcc/core'
import type { OrdersFilter, OrderStatus, OrdersSortBy, OrdersSortOrder, OrderSubCustomerPresence } from './types'
import { ORDER_SUB_CUSTOMER_PRESENCE } from './constants'

// ============================================
// Constants
// ============================================

export const DEFAULT_PAGE_SIZE = 20

export const DEFAULT_ORDERS_SORT = {
  sortBy: 'createdAt' as OrdersSortBy,
  sortOrder: 'desc' as OrdersSortOrder,
}

export const ORDER_STATUS_OPTIONS: Array<{ value: OrderStatus; labelKey: string; color: string }> = [
  { value: 'pending', labelKey: 'orders.pending', color: 'gray' },
  { value: 'processing', labelKey: 'orders.processing', color: 'amber' },
  { value: 'delivered', labelKey: 'orders.delivered', color: 'green' },
  { value: 'cancelled', labelKey: 'orders.cancelled', color: 'red' },
]

export const ORDER_SORT_FIELDS: Array<{
  field: OrdersSortBy
  labelKey: string
  ascLabelKey: string
  descLabelKey: string
}> = [
  {
    field: 'createdAt',
    labelKey: 'orders.sortByDate',
    ascLabelKey: 'orders.sortOldest',
    descLabelKey: 'orders.sortNewest',
  },
  {
    field: 'orderNumber',
    labelKey: 'orders.sortByOrderNumber',
    ascLabelKey: 'orders.sortByOrderNumberAsc',
    descLabelKey: 'orders.sortByOrderNumberDesc',
  },
  {
    field: 'orderDate',
    labelKey: 'orders.sortByOrderDate',
    ascLabelKey: 'orders.sortByOrderDateAsc',
    descLabelKey: 'orders.sortByOrderDateDesc',
  },
  {
    field: 'deliveryDate',
    labelKey: 'orders.sortByDeliveryDate',
    ascLabelKey: 'orders.sortByDeliveryDateAsc',
    descLabelKey: 'orders.sortByDeliveryDateDesc',
  },
  {
    field: 'totalAmount',
    labelKey: 'orders.sortByAmount',
    ascLabelKey: 'orders.sortByAmountAsc',
    descLabelKey: 'orders.sortByAmountDesc',
  },
  {
    field: 'status',
    labelKey: 'orders.sortByStatus',
    ascLabelKey: 'orders.sortByStatusAsc',
    descLabelKey: 'orders.sortByStatusDesc',
  },
  {
    field: 'quantity',
    labelKey: 'orders.quantity',
    ascLabelKey: 'orders.sortByAmountAsc',
    descLabelKey: 'orders.sortByAmountDesc',
  },
  {
    field: 'product',
    labelKey: 'orders.sortByProduct',
    ascLabelKey: 'orders.sortByProductAsc',
    descLabelKey: 'orders.sortByProductDesc',
  },
  {
    field: 'tenant',
    labelKey: 'orders.sortByTenant',
    ascLabelKey: 'orders.sortByTenantAsc',
    descLabelKey: 'orders.sortByTenantDesc',
  },
]

// ============================================
// Default Filter Values
// ============================================

export const DEFAULT_ORDERS_FILTER: OrdersFilter = {
  search: undefined,
  status: [],
  customerId: undefined,
  salesOrderId: undefined,
  dateFrom: undefined,
  dateTo: undefined,
  deliveryDateFrom: undefined,
  deliveryDateTo: undefined,
  products: [],
  deliveryAddresses: [],
  vehicleLicensePlate: undefined,
  driverName: undefined,
  unknownSubCustomer: undefined,
  subCustomerIds: undefined,
  subCustomerPresence: undefined,
  minQuantity: undefined,
  maxQuantity: undefined,
  minLoadingMinutes: undefined,
  maxLoadingMinutes: undefined,
  minProcessingMinutes: undefined,
  maxProcessingMinutes: undefined,
  minAmount: undefined,
  maxAmount: undefined,
  sortBy: DEFAULT_ORDERS_SORT.sortBy,
  sortOrder: DEFAULT_ORDERS_SORT.sortOrder,
  page: 1,
  pageSize: DEFAULT_PAGE_SIZE,
}

// ============================================
// Field Configurations
// ============================================

export const ORDERS_FILTER_FIELDS: FilterFieldConfig[] = [
  {
    id: 'search',
    type: 'text',
    label: 'common.search',
    placeholder: 'orders.searchPlaceholder',
    defaultValue: '',
    debounceMs: 300,
  },
  {
    id: 'vehicleLicensePlate',
    type: 'text',
    label: 'orders.filterByLP',
    placeholder: 'orders.lpPlaceholder',
    defaultValue: '',
    debounceMs: 300,
  },
  {
    id: 'driverName',
    type: 'text',
    label: 'orders.filterByDriver',
    placeholder: 'orders.driverPlaceholder',
    defaultValue: '',
    debounceMs: 300,
  },
  {
    id: 'quantityRange',
    type: 'number-range',
    label: 'orders.filterByQuantity',
    defaultValue: { min: undefined, max: undefined },
    minValue: 0,
  },
  {
    id: 'loadingTimeRange',
    type: 'number-range',
    label: 'orders.filterByLoadingTime',
    defaultValue: { min: undefined, max: undefined },
    minValue: 0,
  },
  {
    id: 'processingTimeRange',
    type: 'number-range',
    label: 'orders.filterByProcessingTime',
    defaultValue: { min: undefined, max: undefined },
    minValue: 0,
  },
  {
    id: 'status',
    type: 'multi-select',
    label: 'orders.filterByStatus',
    placeholder: 'orders.selectStatus',
    defaultValue: [],
    options: ORDER_STATUS_OPTIONS.map((opt) => ({
      value: opt.value,
      label: opt.labelKey,
      color: opt.color,
    })),
    showSelectedCount: true,
  },
  {
    id: 'dateRange',
    type: 'date-range',
    label: 'orders.filterByDate',
    defaultValue: { from: undefined, to: undefined },
  },
  {
    id: 'sort',
    type: 'sort',
    label: 'common.sort',
    defaultValue: { field: DEFAULT_ORDERS_SORT.sortBy, order: DEFAULT_ORDERS_SORT.sortOrder },
    sortFields: ORDER_SORT_FIELDS.map((f) => ({
      field: f.field,
      label: f.labelKey,
      bidirectional: true,
    })),
    excludeFromUrl: false,
  },
]

// ============================================
// Serialization Functions
// ============================================

/**
 * Serialize filter state to URL params
 * Only includes non-default values to keep URLs clean
 */
function serializeOrdersFilter(filter: OrdersFilter): Record<string, string> {
  const params: Record<string, string> = {}

  // Search
  if (filter.search) {
    params.search = filter.search
  }

  // Vehicle License Plate
  if (filter.vehicleLicensePlate) {
    params.vehicleLicensePlate = filter.vehicleLicensePlate
  }

  // Status (multi-select)
  if (filter.status && filter.status.length > 0) {
    params.status = filter.status.join(',')
  }

  // Sales Order ID (for filtered views)
  if (filter.salesOrderId) {
    params.salesOrderId = filter.salesOrderId
  }

  // Date range (order date)
  if (filter.dateFrom) {
    params.dateFrom = filter.dateFrom
  }
  if (filter.dateTo) {
    params.dateTo = filter.dateTo
  }

  // Delivery date range
  if (filter.deliveryDateFrom) {
    params.deliveryDateFrom = filter.deliveryDateFrom
  }
  if (filter.deliveryDateTo) {
    params.deliveryDateTo = filter.deliveryDateTo
  }

  // Products (multi-select)
  if (filter.products && filter.products.length > 0) {
    params.products = filter.products.join(',')
  }

  // Delivery addresses (multi-select)
  if (filter.deliveryAddresses && filter.deliveryAddresses.length > 0) {
    params.deliveryAddresses = filter.deliveryAddresses.join(',')
  }

  // Driver name
  if (filter.driverName) {
    params.driverName = filter.driverName
  }

  // Unknown (placeholder) sub-customer
  if (filter.unknownSubCustomer) {
    params.unknownSubCustomer = 'true'
  }

  // Specific sub-customers + with / without one
  if (filter.subCustomerIds && filter.subCustomerIds.length > 0) {
    params.subCustomers = filter.subCustomerIds.join(',')
  }
  if (filter.subCustomerPresence) {
    params.subCustomerPresence = filter.subCustomerPresence
  }

  // Quantity range (order's own packaging unit)
  if (filter.minQuantity !== undefined) {
    params.minQuantity = String(filter.minQuantity)
  }
  if (filter.maxQuantity !== undefined) {
    params.maxQuantity = String(filter.maxQuantity)
  }

  // Loading time range (minutes)
  if (filter.minLoadingMinutes !== undefined) {
    params.minLoadingMinutes = String(filter.minLoadingMinutes)
  }
  if (filter.maxLoadingMinutes !== undefined) {
    params.maxLoadingMinutes = String(filter.maxLoadingMinutes)
  }

  // Processing time range (minutes)
  if (filter.minProcessingMinutes !== undefined) {
    params.minProcessingMinutes = String(filter.minProcessingMinutes)
  }
  if (filter.maxProcessingMinutes !== undefined) {
    params.maxProcessingMinutes = String(filter.maxProcessingMinutes)
  }

  // Amount range
  if (filter.minAmount !== undefined && filter.minAmount > 0) {
    params.minAmount = String(filter.minAmount)
  }
  if (filter.maxAmount !== undefined && filter.maxAmount > 0) {
    params.maxAmount = String(filter.maxAmount)
  }

  // Sorting (only if different from default)
  if (filter.sortBy && filter.sortBy !== DEFAULT_ORDERS_SORT.sortBy) {
    params.sortBy = filter.sortBy
  }
  if (filter.sortOrder && filter.sortOrder !== DEFAULT_ORDERS_SORT.sortOrder) {
    params.sortOrder = filter.sortOrder
  }

  // Pagination (only if different from defaults)
  if (filter.page && filter.page > 1) {
    params.page = String(filter.page)
  }
  if (filter.pageSize && filter.pageSize !== DEFAULT_PAGE_SIZE) {
    params.pageSize = String(filter.pageSize)
  }

  return params
}

/**
 * Deserialize URL params to partial filter state
 * Returns only fields that are present in URL
 */
function deserializeOrdersFilter(params: Record<string, string | null>): Partial<OrdersFilter> {
  const filter: Partial<OrdersFilter> = {}

  // Search
  if (params.search) {
    filter.search = params.search
  }

  // Vehicle License Plate
  if (params.vehicleLicensePlate) {
    filter.vehicleLicensePlate = params.vehicleLicensePlate
  }

  // Status (comma-separated)
  if (params.status) {
    const statuses = params.status.split(',').filter(Boolean) as OrderStatus[]
    if (statuses.length > 0) {
      filter.status = statuses
    }
  }

  // Sales Order ID
  if (params.salesOrderId) {
    filter.salesOrderId = params.salesOrderId
  }

  // Date range (order date)
  if (params.dateFrom) {
    filter.dateFrom = params.dateFrom
  }
  if (params.dateTo) {
    filter.dateTo = params.dateTo
  }

  // Delivery date range
  if (params.deliveryDateFrom) {
    filter.deliveryDateFrom = params.deliveryDateFrom
  }
  if (params.deliveryDateTo) {
    filter.deliveryDateTo = params.deliveryDateTo
  }

  // Products (comma-separated)
  if (params.products) {
    const products = params.products.split(',').filter(Boolean)
    if (products.length > 0) {
      filter.products = products
    }
  }

  // Delivery addresses (comma-separated)
  if (params.deliveryAddresses) {
    const addresses = params.deliveryAddresses.split(',').filter(Boolean)
    if (addresses.length > 0) {
      filter.deliveryAddresses = addresses
    }
  }

  // Driver name
  if (params.driverName) {
    filter.driverName = params.driverName
  }

  // Unknown (placeholder) sub-customer
  if (params.unknownSubCustomer === 'true') {
    filter.unknownSubCustomer = true
  }

  // Specific sub-customers + with / without one
  if (params.subCustomers) {
    const ids = params.subCustomers.split(',').filter(Boolean)
    if (ids.length > 0) filter.subCustomerIds = ids
  }
  if (isSubCustomerPresence(params.subCustomerPresence)) {
    filter.subCustomerPresence = params.subCustomerPresence
  }

  // Numeric range params (quantity + durations in minutes)
  const NUMERIC_RANGE_KEYS = [
    'minQuantity',
    'maxQuantity',
    'minLoadingMinutes',
    'maxLoadingMinutes',
    'minProcessingMinutes',
    'maxProcessingMinutes',
  ] as const
  for (const key of NUMERIC_RANGE_KEYS) {
    const raw = params[key]
    if (raw === undefined || raw === null || raw === '') continue
    const value = Number(raw)
    if (Number.isFinite(value) && value >= 0) {
      filter[key] = value
    }
  }

  // Amount range
  if (params.minAmount) {
    const minAmount = Number(params.minAmount)
    if (!isNaN(minAmount) && minAmount > 0) {
      filter.minAmount = minAmount
    }
  }
  if (params.maxAmount) {
    const maxAmount = Number(params.maxAmount)
    if (!isNaN(maxAmount) && maxAmount > 0) {
      filter.maxAmount = maxAmount
    }
  }

  // Sorting
  if (params.sortBy) {
    const sortBy = params.sortBy as OrdersSortBy
    const validSortFields: OrdersSortBy[] = ['createdAt', 'orderDate', 'deliveryDate', 'orderNumber', 'status', 'quantity', 'totalAmount', 'product', 'tenant']
    if (validSortFields.includes(sortBy)) {
      filter.sortBy = sortBy
    }
  }
  if (params.sortOrder) {
    const sortOrder = params.sortOrder as OrdersSortOrder
    if (sortOrder === 'asc' || sortOrder === 'desc') {
      filter.sortOrder = sortOrder
    }
  }

  // Pagination
  if (params.page) {
    const page = Number(params.page)
    if (Number.isInteger(page) && page > 0) {
      filter.page = page
    }
  }
  if (params.pageSize) {
    const pageSize = Number(params.pageSize)
    if (Number.isInteger(pageSize) && pageSize > 0 && pageSize <= 100) {
      filter.pageSize = pageSize
    }
  }

  return filter
}

/**
 * Validate and sanitize filter values
 */
const SUB_CUSTOMER_PRESENCE_VALUES: readonly unknown[] = Object.values(ORDER_SUB_CUSTOMER_PRESENCE)

function isSubCustomerPresence(value: unknown): value is OrderSubCustomerPresence {
  return SUB_CUSTOMER_PRESENCE_VALUES.includes(value)
}

function validateOrdersFilter(filter: Partial<OrdersFilter>): OrdersFilter {
  const noSubCustomer = filter.subCustomerPresence === ORDER_SUB_CUSTOMER_PRESENCE.NONE
  const validStatuses: OrderStatus[] = ['pending', 'processing', 'delivered', 'cancelled']
  const validSortFields: OrdersSortBy[] = ['createdAt', 'orderDate', 'deliveryDate', 'orderNumber', 'status', 'quantity', 'totalAmount', 'product', 'tenant']

  /** Keep only finite, non-negative numbers; anything else is treated as absent. */
  const normalizeBound = (value: unknown): number | undefined => {
    if (value === undefined || value === null || value === '') return undefined
    const num = Number(value)
    return Number.isFinite(num) && num >= 0 ? num : undefined
  }

  /** Normalize a min/max pair, swapping if the user entered them backwards. */
  const normalizeRange = (
    rawMin: unknown,
    rawMax: unknown
  ): [number | undefined, number | undefined] => {
    const min = normalizeBound(rawMin)
    const max = normalizeBound(rawMax)
    if (min !== undefined && max !== undefined && min > max) return [max, min]
    return [min, max]
  }

  const [minQuantity, maxQuantity] = normalizeRange(filter.minQuantity, filter.maxQuantity)
  const [minLoadingMinutes, maxLoadingMinutes] = normalizeRange(
    filter.minLoadingMinutes,
    filter.maxLoadingMinutes
  )
  const [minProcessingMinutes, maxProcessingMinutes] = normalizeRange(
    filter.minProcessingMinutes,
    filter.maxProcessingMinutes
  )

  return {
    ...DEFAULT_ORDERS_FILTER,
    ...filter,
    // Ensure status values are valid
    status: (filter.status || []).filter((s) => validStatuses.includes(s)),
    // Ensure products is an array
    products: filter.products || [],
    // Ensure deliveryAddresses is an array
    deliveryAddresses: filter.deliveryAddresses || [],
    // Normalize optional string fields: empty strings are treated as absent so
    // Redux state and URL-parsed state stay in sync (the URL omits empty values).
    search: String(filter.search ?? '').trim() || undefined,
    customerId: filter.customerId?.trim() || undefined,
    salesOrderId: filter.salesOrderId?.trim() || undefined,
    dateFrom: filter.dateFrom?.trim() || undefined,
    dateTo: filter.dateTo?.trim() || undefined,
    vehicleLicensePlate: filter.vehicleLicensePlate?.trim() || undefined,
    driverName: filter.driverName?.trim() || undefined,
    // "No sub-customer" excludes the other sub-customer filters, so it wins over them.
    unknownSubCustomer: noSubCustomer ? undefined : filter.unknownSubCustomer === true || undefined,
    subCustomerIds: noSubCustomer || !filter.subCustomerIds?.length ? undefined : [...filter.subCustomerIds],
    subCustomerPresence: isSubCustomerPresence(filter.subCustomerPresence) ? filter.subCustomerPresence : undefined,
    // Numeric ranges: non-negative, finite, and min/max swapped if inverted
    minQuantity,
    maxQuantity,
    minLoadingMinutes,
    maxLoadingMinutes,
    minProcessingMinutes,
    maxProcessingMinutes,
    // Ensure amount range is valid
    minAmount: filter.minAmount !== undefined && filter.minAmount > 0 ? filter.minAmount : undefined,
    maxAmount: filter.maxAmount !== undefined && filter.maxAmount > 0 ? filter.maxAmount : undefined,
    // Delivery date fields — empty strings treated as absent
    deliveryDateFrom: filter.deliveryDateFrom?.trim() || undefined,
    deliveryDateTo: filter.deliveryDateTo?.trim() || undefined,
    // Ensure sort field is valid
    sortBy: validSortFields.includes(filter.sortBy as OrdersSortBy)
      ? (filter.sortBy as OrdersSortBy)
      : DEFAULT_ORDERS_SORT.sortBy,
    // Ensure sort order is valid
    sortOrder: filter.sortOrder === 'asc' || filter.sortOrder === 'desc'
      ? filter.sortOrder
      : DEFAULT_ORDERS_SORT.sortOrder,
    // Ensure page is positive
    page: Math.max(1, filter.page ?? 1),
    // Ensure pageSize is in valid range
    pageSize: Math.min(100, Math.max(1, filter.pageSize ?? DEFAULT_PAGE_SIZE)),
  }
}

// ============================================
// Export Configuration
// ============================================

export const ordersFilterConfig = createFilterConfig<OrdersFilter>({
  domainName: 'orders',
  fields: ORDERS_FILTER_FIELDS,
  defaults: DEFAULT_ORDERS_FILTER,
  serialize: serializeOrdersFilter,
  deserialize: deserializeOrdersFilter,
  validate: validateOrdersFilter,
  paramMapping: {},
})

// ============================================
// URL Search Params Type (for TanStack Router)
// ============================================

/**
 * Type for TanStack Router validateSearch
 */
export interface OrdersSearchParams {
  search?: string
  status?: string
  salesOrderId?: string
  dateFrom?: string
  dateTo?: string
  deliveryDateFrom?: string
  deliveryDateTo?: string
  products?: string
  deliveryAddresses?: string
  vehicleLicensePlate?: string
  driverName?: string
  unknownSubCustomer?: string
  subCustomers?: string
  subCustomerPresence?: string
  minQuantity?: string
  maxQuantity?: string
  minLoadingMinutes?: string
  maxLoadingMinutes?: string
  minProcessingMinutes?: string
  maxProcessingMinutes?: string
  minAmount?: string
  maxAmount?: string
  sortBy?: string
  sortOrder?: string
  page?: string
  pageSize?: string
  openOrderNumber?: string
}

/**
 * Validate search params for TanStack Router
 */
export function validateOrdersSearch(search: Record<string, unknown>): OrdersSearchParams {
  return {
    search: typeof search.search === 'string' ? search.search : undefined,
    status: typeof search.status === 'string' ? search.status : undefined,
    salesOrderId: typeof search.salesOrderId === 'string' ? search.salesOrderId : undefined,
    dateFrom: typeof search.dateFrom === 'string' ? search.dateFrom : undefined,
    dateTo: typeof search.dateTo === 'string' ? search.dateTo : undefined,
    deliveryDateFrom: typeof search.deliveryDateFrom === 'string' ? search.deliveryDateFrom : undefined,
    deliveryDateTo: typeof search.deliveryDateTo === 'string' ? search.deliveryDateTo : undefined,
    products: typeof search.products === 'string' ? search.products : undefined,
    deliveryAddresses: typeof search.deliveryAddresses === 'string' ? search.deliveryAddresses : undefined,
    vehicleLicensePlate: typeof search.vehicleLicensePlate === 'string' ? search.vehicleLicensePlate : undefined,
    driverName: typeof search.driverName === 'string' ? search.driverName : undefined,
    // TanStack Router JSON-parses search values, so `?unknownSubCustomer=true` arrives as a boolean
    unknownSubCustomer: String(search.unknownSubCustomer) === 'true' ? 'true' : undefined,
    subCustomers: typeof search.subCustomers === 'string' ? search.subCustomers : undefined,
    subCustomerPresence: isSubCustomerPresence(search.subCustomerPresence) ? search.subCustomerPresence : undefined,
    minQuantity: typeof search.minQuantity === 'string' ? search.minQuantity : undefined,
    maxQuantity: typeof search.maxQuantity === 'string' ? search.maxQuantity : undefined,
    minLoadingMinutes: typeof search.minLoadingMinutes === 'string' ? search.minLoadingMinutes : undefined,
    maxLoadingMinutes: typeof search.maxLoadingMinutes === 'string' ? search.maxLoadingMinutes : undefined,
    minProcessingMinutes: typeof search.minProcessingMinutes === 'string' ? search.minProcessingMinutes : undefined,
    maxProcessingMinutes: typeof search.maxProcessingMinutes === 'string' ? search.maxProcessingMinutes : undefined,
    minAmount: typeof search.minAmount === 'string' ? search.minAmount : undefined,
    maxAmount: typeof search.maxAmount === 'string' ? search.maxAmount : undefined,
    sortBy: typeof search.sortBy === 'string' ? search.sortBy : undefined,
    sortOrder: typeof search.sortOrder === 'string' ? search.sortOrder : undefined,
    page: typeof search.page === 'string' ? search.page : undefined,
    pageSize: typeof search.pageSize === 'string' ? search.pageSize : undefined,
    openOrderNumber: typeof search.openOrderNumber === 'string' ? search.openOrderNumber : undefined,
  }
}
